import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import {
  describeDetection,
  EMAIL_SENDERS_HELP_PATH,
  type EmailProviderDetection,
} from "@/lib/email/detect-provider";

interface Props {
  /** Called with each new result (null while a check is running or after an error). */
  onResult?: (d: EmailProviderDetection | null) => void;
  className?: string;
}

/** "Not sure which? Enter your email" -- calls email-provider-detect and shows the verdict. */
export function EmailProviderCheck({ onResult, className }: Props) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EmailProviderDetection | null>(null);
  const [error, setError] = useState<string | null>(null);

  const check = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    onResult?.(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke<EmailProviderDetection>(
        "email-provider-detect",
        { body: { email: email.trim() } },
      );
      if (fnError) {
        // Non-2xx: the friendly message is in the response body (error.context).
        let message: string | undefined;
        try {
          message = (await (fnError as { context?: Response }).context?.json())?.message;
        } catch { /* not JSON */ }
        throw new Error(message || "We can't check right now. Please try again in a minute.");
      }
      if (!data) throw new Error("We can't check right now. Please try again in a minute.");
      setResult(data);
      onResult?.(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const verdict = result && describeDetection(result);

  return (
    <div className={cn("space-y-3", className)}>
      <form onSubmit={check} className="flex gap-2">
        <Input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@yourcompany.com"
          aria-label="Your email address"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            // A verdict (and the highlighted Connect card) belongs to the address it was checked for.
            if (result || error) {
              setResult(null);
              setError(null);
              onResult?.(null);
            }
          }}
        />
        <Button type="submit" variant="outline" disabled={loading || !email.trim()}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}
        </Button>
      </form>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {verdict && (
        <div role="status" className="rounded-lg border bg-card p-4 text-sm">
          <p className="font-medium">{verdict.title}</p>
          <p className="text-muted-foreground">{verdict.body}</p>
          {!result?.suggestedProvider && result?.reason !== "invalid_email" && (
            <p className="mt-2">
              <Link to={EMAIL_SENDERS_HELP_PATH} className="underline underline-offset-2">
                How to tell which one you have
              </Link>
            </p>
          )}
          {result?.suggestedProvider && (
            <p className="mt-2 text-xs text-muted-foreground">
              Not right? Choose the one you sign in to on the web.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
